import React, { useEffect, useState, useCallback } from 'react';
import useForm from '@utils/useForm';
import { find, get, isEmpty, isNil } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { updateBasicDetails, updateOccupation, updateOccupationCode, updateGeneralQuestions } from '@redux/slices/DPI/step1Slice';
import { BasicDetailsForm } from '@models/DPI/BasicDetails.model';
import { updateDPIOccupations } from '@redux/slices/staticSlice';
import { getOccupations } from '@api/index';
import { GAAviva } from '@utils/gaAviva';

import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import Heading from '@components/Heading';
import InformationBox from '@components/InformationBox';
import RadioButtonGroup from '@components/RadioButtonGroup';
import RadioButton from '@components/RadioButton';
import Link from '@components/Link';
import Dropdown from '@components/Dropdown';
import CheckboxList from '@components/CheckboxList';
import Checkbox from '@components/Checkbox';
import MultiSteps from '@components/MultiSteps';
import Text from '@components/Text';
import Button from '@components/Button';
import ContactBox from '@components/ContactBox';
import DayOfBirth from './components/DayOfBirth';
import Bullet from '@components/Bullet';
import PlanInformation from './components/PlanInformation';
import Divider from '@components/Divider';

import { validate } from './service';
import { Helpers } from '@utils/helpers';

const page = 'Basic Details';

const trackOnload = (fromData: BasicDetailsForm, errorMessage: string = ""): void => {
  const { month, day, year } = fromData.dob;
  const stringDate = `${month}/${day}/${year}`;
  const age = Helpers.getAge(new Date(stringDate));
  const gender = Helpers.getGenderNameByCode(fromData.gender || '');
  const GA = GAAviva.getInstance();
  let { user, form, page } = GA.digitalData;
  user = { ...user, person: { age: `${age}`}}
  form = { ...form,
    name: 'Basic Details',
    field: { ...form?.field, Gender: gender}
  };
  GA.trackPageLoad({ user, form, page });
}
const trackFormSubmmit = (): void =>  {
  const GA = GAAviva.getInstance();
  GA.trackFormSuccess({});
}

// Should be exported to seperate files;
export interface BasicDetailsProps { }

function BasicDetails() {
  console.log(`Aviva - ${page}`);
  const history = useHistory();
  const dispatch = useAvivaDispatch();

  const brochureUrl = useAvivaSelector(state => state.staticData.brochureUrl);

  const initValue = useAvivaSelector(state => state.dpiStep1.basicDetails);
  const occupations = useAvivaSelector(state => state.staticData.dpiOccupations);
  const nationlaities = useAvivaSelector(state => state.staticData.nationlaities);
  const contactInformation = useAvivaSelector(state => state.staticData.contactInformation);
  const [genericError, setGenericError] = useState<string | undefined>(undefined);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);

  const getOccupationsListing = useCallback(async () => {
    try {
      const response = await getOccupations('dpi');
      const list = get(response, 'data.occupations', []);
      dispatch(updateDPIOccupations(list))
    } catch (error) {
      console.error('getOccupationsListing', error);
      setGenericError("Couldn't get Occupations");
    }
  }, [dispatch]);

  const urlParams: URLSearchParams = Helpers.getURLSearchParams();

  const getGenericError = useCallback(() => {
    const dobError = urlParams.get('doberror');
    const genderError = urlParams.get('gendererror');
    const nationalityError = urlParams.get('nationalityerror');

    if (dobError === 'true') {
      setGenericError('Please ensure date of birth matches with Myinfo data.');
    }
    if (genderError === 'true') {
      setGenericError('Please ensure gender matches with Myinfo data.');
    }
    if (nationalityError === 'true') {
      setGenericError('Please ensure nationality matches with Myinfo data.');
    }
  }, [urlParams])

  const { handleChange, values, errors, formValid } = useForm(new BasicDetailsForm(initValue), () => { }, validate);

  const onContinue = () => {
    const selectedOccupation = find(occupations, { desc: values['occupationName'] });

    const { hasExistingInsurance, applyingOrIntending } = values;
    if ((selectedOccupation && selectedOccupation.status !== 'Standard') && selectedOccupation.status !== 'Y') {
      gotoContact();
      return;
    }
    if (hasExistingInsurance === 'Y' || applyingOrIntending === 'Y') {
      history.push('/dpi/contact');
      return;
    }
    const generalQuestions = `19,${hasExistingInsurance}|20,${applyingOrIntending}`;
    console.debug(generalQuestions);
    dispatch(updateGeneralQuestions(generalQuestions));
   
    console.debug({ selectedOccupation, values });
    dispatch(updateBasicDetails(values));
    dispatch(updateOccupation(selectedOccupation || { code: '', desc: ''}));
    dispatch(updateOccupationCode(selectedOccupation?.code || ''));
    trackOnload(values);
    trackFormSubmmit();
    history.push('/dpi/coverage')
  }

  const gotoContact = () => {
    history.push('/dpi/contact')
  }

  useEffect(() => {
    if (!isPageLoading) {
      getGenericError();
      if (!occupations) {
        getOccupationsListing();
      }
      trackOnload(values);
      setIsPageLoading(true);
    }
    
  }, [occupations, getOccupationsListing, urlParams, getGenericError, isPageLoading, values]);

  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
          <MultiSteps steps={5} step={1} label="Let's get a quick quote..." />
          <Heading margin="0 0 30px"> Let&apos;s get a quick quote... </Heading>
        </FullGrid>

        <LeftGrid>
          {!isEmpty(genericError) && !isNil(genericError) && (
            <InformationBox variant="error">
              <Text>{genericError}</Text>
            </InformationBox>
          )}
          <form onSubmit={e => { e.preventDefault() }} noValidate>
            <Heading variant="label"> Date of birth </Heading>
            {!errors?.dob?.isValid && errors?.dob?.message && (
              <Bullet variant="error">{errors?.dob?.message}</Bullet>
            )}
            <DayOfBirth error={!errors?.dob?.isValid && errors?.dob?.message} value={values['dob']} name="dob" onChange={handleChange} />
            <Heading variant="label"> Gender </Heading>
            <RadioButtonGroup value={values['gender']} name="gender" onChange={handleChange}>
              <RadioButton text="Male" value='M' />
              <RadioButton text="Female" value='F' />
            </RadioButtonGroup>

            <Heading variant="label"> Are you a smoker? </Heading>
            <RadioButtonGroup value={values['smoker']} name="smoker" onChange={handleChange}>
              <RadioButton text="Yes" value='Y' />
              <RadioButton text="No" value='N' />
            </RadioButtonGroup>

            <Heading variant="label"> Occupation </Heading>
            <Dropdown variant="auto-complete" value={values['occupationName']} name="occupationName" onChange={handleChange} source={occupations || []} bindLabel='desc' bindId='desc' />

            <Heading variant="label"> Nationality </Heading>
            <Dropdown variant="auto-complete"  value={values['nationalityCode']} name="nationalityCode" onChange={handleChange} source={nationlaities} bindId='code' bindLabel='description' />

            <InformationBox hasClose>
              <Text> Don’t see your nationality? Please contact us <Link text="here" variant="button" onClick={gotoContact}></Link></Text>
            </InformationBox>

            <Heading variant="label"> Do you have any existing life insurance policy(ies)? </Heading>
            <RadioButtonGroup value={values['hasExistingInsurance']} name="hasExistingInsurance" onChange={handleChange}>
              <RadioButton text="Yes" value='Y'/>
              <RadioButton text="No" value='N'/>
            </RadioButtonGroup>

            <Heading variant="label"> Are you currently applying or intending to apply for any other policies with any insurance company(ies)?</Heading>
            <RadioButtonGroup value={values['applyingOrIntending']} name="applyingOrIntending" onChange={handleChange}>
              <RadioButton text="Yes" value='Y'/>
              <RadioButton text="No" value='N'/>
            </RadioButtonGroup>

            <Heading variant="sub" margin="0 0 15px"> Please tick and confirm </Heading>
            <Divider margin="0 0 32px" />
            <CheckboxList>
              <Checkbox margin="0 0 30px" name="required1" checked={values['required1']} onChange={handleChange}>
                <Text> I am a Singapore Resident with a valid NRIC or FIN*.</Text>
              </Checkbox>
              <Checkbox margin="0 0 30px" name="required2" checked={values['required2']} onChange={handleChange}>
                <Text> I have resided in Singapore for more than 183 days in the last 12 months.</Text>
              </Checkbox>
              <Checkbox margin="0 0 30px" name="required3" checked={values['required3']} onChange={handleChange}>
                <Text> I am not an undischarged bankrupt.</Text>
              </Checkbox>
              <Checkbox margin="0 0 30px" name="required4" checked={values['required4']} onChange={handleChange}>
                <Text> I am not purchasing this policy to replace any existing policy with Singapore Life Ltd. (“Singlife”) or other insurer(s).</Text>
              </Checkbox>
            </CheckboxList>
            <Heading variant='desc'>
              *Employment Pass / S Pass / Dependant Pass / Student Pass / Long Term Visit Pass / Work Permit (exclude foreign domestic helper and construction worker)
            </Heading>

          </form>

          <Button margin="40px 0 40px" disabled={!formValid} label="Continue" onClick={onContinue}></Button>

        </LeftGrid>

        <EmptyGrid></EmptyGrid>

        <RightGrid>
          <PlanInformation brochureUrl={brochureUrl} />
          <ContactBox items={contactInformation} />
        </RightGrid>
      </ContainerGrid>
    </Container>
  );
};

export default BasicDetails;
